import { useEffect, useState } from 'react';
import { Button, Icon, Modal, SkeletonBodyText, TextField } from '@shopify/polaris';
import { XIcon, AlertTriangleIcon } from '@shopify/polaris-icons';
import Language from '../../i18n/Language';
import {
    reqGetAccount,
    reqUpdExcessLimit,
    reqHandleAIUsagePayment,
    reqHandleImageUsagePayment,
} from '../../api';
import { store } from '../../redux/store';
import { setPageData } from '../../redux/actions/getAppPageData';
import './index.scss';

// 账户额度缓存 key（与 Settings 页面共用，减少重复请求）
const ACCOUNT_CACHE_KEY = 'settingAcount';

const t = Language;

// 语言 -> 日期本地化
const LANG_LIST = {
    cs: 'cs-CZ', en: 'en-GB', da: 'da-DK', es: 'es-ES', fr: 'fr-FR', de: 'de-DE',
    in: 'hi-IN', it: 'it-IT', nl: 'nl-NL', nb: 'no-NO', pl: 'pl-PL', pt: 'pt-PT',
    pt_BR: 'pt-BR', fi: 'fi-FI', sv: 'sv-SE', tr: 'tr-TR', th: 'th-TH', ja: 'ja-JP',
    ko: 'ko-KR', zh: 'zh-CN', zh_hant: 'zh-TW',
};

// 额度重置日期：18 March 2026
const formatResetDate = (timestamp, lang = 'en') => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString(LANG_LIST[lang] || 'en-GB', options);
};

/**
 * 可复用的超额提示弹窗（自包含超额开启逻辑）
 * @param {boolean} open            是否打开
 * @param {function} onClose        关闭回调
 * @param {'image'|'points'} type   触发场景：image=图片额度触发 / points=AI 积分触发
 * @param {number} points           开启超额需扣除/授权的额度（AI 传积分数，如 20）
 * @param {object} payParams        自定义超额扣费接口入参（默认 { points }）
 * @param {boolean} isTrial         是否试用期用户（展示试用超额提示 Banner）
 * @param {string} language         当前语言（用于日期本地化）
 * @param {function} onSuccess      开启超额成功回调（触发页可用于刷新/重试）
 * @param {function} onWaitForReset 点击「Wait for reset」
 * @param {function} onContact      点击「Contact us」
 * @param {function} toggleToast    全局 toast 提示
 */
const OverageModal = (props) => {
    const {
        open,
        onClose,
        type = 'image',
        points = 0,
        payParams,
        isTrial = false,
        language = 'en',
        onSuccess,
        onWaitForReset,
        onContact,
        toggleToast,
    } = props;

    // 是否 AI 积分场景（兼容旧的 'ai' 取值）
    const isAi = type === 'points' || type === 'ai';

    const [loading, setLoading] = useState(true);   // 首次无缓存时的骨架屏
    const [refreshing, setRefreshing] = useState(false); // 静默刷新中（按钮需等新数据返回才可点击）
    // 月度图片额度（已用/总额）
    const [imagesUse, setImagesUse] = useState(0);
    const [imagesAll, setImagesAll] = useState(0);
    // 月度 AI 积分额度（已用/总额）
    const [pointsUse, setPointsUse] = useState(0);
    const [pointsAll, setPointsAll] = useState(0);
    // 额度重置日期
    const [resetDate, setResetDate] = useState('');
    // 当前账单设置的超额上限 / 输入值
    const [selectCap, setSelectCap] = useState(200);
    const [originCap, setOriginCap] = useState(200);
    // 超额错误提示 & 是否达到 Shopify 上限
    const [overchargeError, setOverchargeError] = useState('');
    const [isLimitMax, setIsLimitMax] = useState(false);
    // 请求中状态
    const [submitting, setSubmitting] = useState(false); // 开启超额
    const [updating, setUpdating] = useState(false);      // 更新上限

    // 向上调整后展示 Update Spending limit 按钮
    const isShowUpdate = Number(selectCap) > Number(originCap);

    // 将账户数据填充到展示状态
    const applyAccountData = (res) => {
        if (!res || !res.meal_data) return;
        const m = res.meal_data;
        setImagesUse(Number(m.images_use || 0));
        setImagesAll(Number(m.images_all || 0));
        setPointsUse(Number(m.points_gift_use || 0));
        setPointsAll(Number(m.points_gift_all || 0));
        setResetDate(formatResetDate(res.reset_quota * 1000, language));
        const cap = Number(res.capped_amount || 200);
        setSelectCap(cap);
        setOriginCap(cap);
        setIsLimitMax(Boolean(res.is_excess_limit));
    };

    // 打开时：先用缓存展示，再静默请求最新数据更新
    const getAccount = async () => {
        const cache = store.getState()?.page?.pageInit?.[ACCOUNT_CACHE_KEY];
        if (cache && cache.meal_data) {
            applyAccountData(cache); // 缓存命中，立即展示，跳过骨架屏
            setLoading(false);
        } else {
            setLoading(true);
        }
        // 静默刷新：按钮需等新数据返回后才可点击
        setRefreshing(true);
        const res = await reqGetAccount();
        if (res && res.meal_data) {
            store.dispatch(setPageData([ACCOUNT_CACHE_KEY, res])); // 更新缓存
            applyAccountData(res);
        }
        setLoading(false);
        setRefreshing(false);
    };

    useEffect(() => {
        if (open) {
            setOverchargeError('');
            getAccount();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // 输入超额（参考 Settings/indexClass handleOverchargeChange）
    const handleOverchargeChange = (e) => {
        setSelectCap(e);
        setOverchargeError('');
    };

    // 步进器（参考 Settings/indexClass onSpinnerOvercharge）
    const onSpinnerOvercharge = (e) => {
        const _n = Number(selectCap);
        const newCap = _n < Number(e) ? _n + 100 : _n < 100 ? _n - 1 : _n - 100;
        if (newCap >= 0) {
            setSelectCap(newCap);
            setOverchargeError('');
        }
    };

    // 校验并更新超额上限（参考 Settings/indexClass changeCapValue）
    const handleUpdateLimit = async () => {
        if (Number(selectCap) < Number(originCap)) {
            setOverchargeError(t('Please enter an amount above ${{v1}} USD', originCap));
            return;
        }
        setOverchargeError('');
        setUpdating(true);
        const res = await reqUpdExcessLimit({ amount: Number(selectCap) });
        setUpdating(false);
        if (res && res.ack === 1) {
            setOriginCap(Number(selectCap));
            if (res.data && res.data.confirmationUrl) {
                setTimeout(() => {
                    window.open(res.data.confirmationUrl);
                }, 500);
            }
        } else {
            setOverchargeError((res && res.msg) || '');
        }
    };

    // 开启超额扣费（AI: reqHandleAIUsagePayment / 图片: reqHandleImageUsagePayment）
    const handleTurnOnOverage = async () => {
        setSubmitting(true);
        // AI 需传 points；图片默认无入参（与原逻辑一致），如需可通过 payParams 覆盖
        const params = payParams !== undefined ? payParams : (isAi ? { points } : undefined);
        const res = isAi
            ? await reqHandleAIUsagePayment(params)
            : await reqHandleImageUsagePayment(params);
        setSubmitting(false);
        if (res && res.ack === 1) {
            onSuccess && onSuccess(res);
            onClose && onClose();
        } else {
            toggleToast && toggleToast((res && res.msg) || '', true);
        }
    };

    // 标题 & 描述文案（根据触发场景切换）
    const title = isAi
        ? t("You've used your included AI credits")
        : t("You've used your included image quota");

    const descTail = isAi
        ? t('to keep using AI features now.')
        : t('to keep optimizing images now.');

    // 进度条百分比 & 是否用尽
    const imagePct = imagesAll > 0 ? Math.min(100, (imagesUse / imagesAll) * 100) : 0;
    const pointsPct = pointsAll > 0 ? Math.min(100, (pointsUse / pointsAll) * 100) : 0;
    const imageRunOut = imagesAll > 0 && imagesUse >= imagesAll;
    const pointsRunOut = pointsAll > 0 && pointsUse >= pointsAll;

    // 用量卡片
    const usageCard = (label, use, all, pct, runOut) => (
        <div className="om_card">
            <p className="om_card_title">{label}</p>
            <div className="om_progress">
                <div className="om_progress_bar" style={{ width: `${pct}%` }} />
            </div>
            <div className={`om_card_usage${runOut ? ' is_critical' : ''}`}>
                <span className="om_card_total">{t('{{v1}} of {{v2}} used', use, all)}</span>
            </div>
        </div>
    );

    return (
        <Modal open={Boolean(open)} title={title} onClose={onClose}>
            <div className="overage_modal_content">
                {/* Content */}
                <div className="om_content">
                    {loading ? (
                        <SkeletonBodyText lines={8} />
                    ) : (
                        <>
                            <p className="om_desc">
                                {t('Your quota resets on')}{' '}
                                <b className="om_desc_date">{resetDate}</b>.&nbsp;
                                {t('You can wait for the reset at no cost, or turn on overage billing')} {descTail}
                            </p>

                            {/* 试用期用户超额提示 */}
                            {isTrial && (
                                <div className="om_banner">
                                    <span className="om_banner_icon">
                                        <Icon source={AlertTriangleIcon} />
                                    </span>
                                    <div className="om_banner_text">
                                        <p className="om_banner_title">
                                            {t('Overage charges can apply during your free trial')}
                                        </p>
                                        <p className="om_banner_desc">
                                            {t('Your free trial covers the subscription fee only. Usage beyond your included credits is billed separately, even during the trial.')}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Monthly Usage */}
                            <div className="om_usage">
                                <div className="om_usage_header">
                                    <span className="om_usage_title">{t('Monthly Usage')}</span>
                                    <span className="om_usage_reset">{t('Resets {{v1}}', resetDate)}</span>
                                </div>
                                <div className="om_usage_cards">
                                    {usageCard(t('Image Quota'), imagesUse, imagesAll, imagePct, imageRunOut)}
                                    {usageCard(t('AI Credits'), pointsUse, pointsAll, pointsPct, pointsRunOut)}
                                </div>
                                <div className="om_pricing">
                                    <p>{t('Images Quota: $4.99 per 1,000 images')}</p>
                                    <p>{t('AI credits: $9.90 per 100 credits')}</p>
                                </div>
                            </div>

                            {/* Monthly Spending limit（参考 Settings/indexClass 1256-1281） */}
                            <div className="om_spending">
                                <p className="om_spending_title">{t('Monthly Spending limit')}</p>
                                <p className="om_spending_desc">
                                    {t('Set your spending limit for additional usage.')}{' '}
                                    <span className="om_link" onClick={onContact}>
                                        {t('Questions about charges? Contact us.')}
                                    </span>
                                </p>
                                <div className="om_spending_input">
                                    <div className="overcharg_setting">
                                        <TextField
                                            type="number"
                                            value={selectCap}
                                            onChange={handleOverchargeChange}
                                            onSpinnerChange={onSpinnerOvercharge}
                                            disabled={loading || refreshing}
                                            prefix="$"
                                            autoComplete="off"
                                        />
                                    </div>
                                    {isShowUpdate && (
                                        <Button onClick={handleUpdateLimit} loading={updating} disabled={refreshing}>
                                            {t('Update Spending limit')}
                                        </Button>
                                    )}
                                </div>
                                {overchargeError || Number(selectCap) < Number(originCap) ? (
                                    <p className="om_spending_error">
                                        {overchargeError || t('Please enter an amount above ${{v1}} USD', originCap)}
                                    </p>
                                ) : isLimitMax ? (
                                    <p className="om_spending_error">
                                        {t('The App spending limit has been reached, no further charges can be made.')}
                                    </p>
                                ) : isShowUpdate ? (
                                    <p className="om_spending_hint">
                                        {t("You'll be redirected to Shopify to approve the new limit")}
                                    </p>
                                ) : null}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="om_footer">
                    <Button disabled={loading || refreshing || submitting} onClick={onWaitForReset}>
                        {t('Wait for reset')}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleTurnOnOverage}
                        loading={submitting}
                        disabled={loading || refreshing}
                    >
                        {t('Turn on overage billing')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default OverageModal;
